import type { Locale } from '@/config/locales';
import type { Language, CookingTimeTranslations, CookingTimeData } from './types';

// #522 — Locale 단일 출처 정합 (15언어 전수). 누락 시 컴파일 에러.
export const translations:
  & Record<Language, CookingTimeTranslations>
  & Record<Exclude<Locale, Language>, CookingTimeTranslations> = {
  ko: {
    title: '평균 조리 시간',
    subtitle: '요일별 · 시간대별 평균 조리 시간을 확인하세요',
    today: '오늘',
    statusGuide: '상태 안내',
    statusDescriptions: {
      quiet: '대기시간이 짧습니다',
      normal: '평균 대기시간입니다',
      peak: '주문이 많아 대기시간이 깁니다'
    },
    days: {
      monday: '월요일',
      tuesday: '화요일',
      wednesday: '수요일',
      thursday: '목요일',
      friday: '금요일',
      saturday: '토요일',
      sunday: '일요일'
    },
    timeSlots: {
      morning: '오전 (6-12시)',
      afternoon: '오후 (12-18시)',
      evening: '저녁 (18-24시)',
      lateNight: '심야 (24-6시)'
    },
    minutes: '분',
    peak: '피크',
    normal: '보통',
    quiet: '한적'
  },
  es: {
    title: 'Tiempo Promedio de Cocción',
    subtitle: 'Consulta los tiempos promedio de cocción por día y hora',
    today: 'Hoy',
    statusGuide: 'Guía de Estados',
    statusDescriptions: {
      quiet: 'Tiempo de espera corto',
      normal: 'Tiempo de espera promedio',
      peak: 'Muchos pedidos, tiempo de espera largo'
    },
    days: {
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo'
    },
    timeSlots: {
      morning: 'Mañana (6-12h)',
      afternoon: 'Tarde (12-18h)',
      evening: 'Noche (18-24h)',
      lateNight: 'Madrugada (24-6h)'
    },
    minutes: 'min',
    peak: 'Pico',
    normal: 'Normal',
    quiet: 'Tranquilo'
  },
  en: {
    title: 'Average Cooking Time',
    subtitle: 'Check average cooking times by day and hour',
    today: 'Today',
    statusGuide: 'Status Guide',
    statusDescriptions: {
      quiet: 'Short waiting time',
      normal: 'Average waiting time',
      peak: 'Many orders, long waiting time'
    },
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },
    timeSlots: {
      morning: 'Morning (6-12h)',
      afternoon: 'Afternoon (12-18h)',
      evening: 'Evening (18-24h)',
      lateNight: 'Late Night (24-6h)'
    },
    minutes: 'min',
    peak: 'Peak',
    normal: 'Normal',
    quiet: 'Quiet'
  },
  pt: {
    title: 'Tempo Médio de Preparo',
    subtitle: 'Confira o tempo médio de preparo por dia e horário',
    today: 'Hoje',
    statusGuide: 'Guia de Status',
    statusDescriptions: {
      quiet: 'Tempo de espera curto',
      normal: 'Tempo de espera médio',
      peak: 'Muitos pedidos, tempo de espera longo'
    },
    days: {
      monday: 'Segunda-feira',
      tuesday: 'Terça-feira',
      wednesday: 'Quarta-feira',
      thursday: 'Quinta-feira',
      friday: 'Sexta-feira',
      saturday: 'Sábado',
      sunday: 'Domingo'
    },
    timeSlots: {
      morning: 'Manhã (6-12h)',
      afternoon: 'Tarde (12-18h)',
      evening: 'Noite (18-24h)',
      lateNight: 'Madrugada (24-6h)'
    },
    minutes: 'min',
    peak: 'Pico',
    normal: 'Normal',
    quiet: 'Tranquilo'
  },
  fr: {
    title: 'Temps de préparation moyen',
    subtitle: 'Consultez les temps de préparation moyens par jour et par heure',
    today: "Aujourd'hui",
    statusGuide: 'Guide des statuts',
    statusDescriptions: {
      quiet: "Temps d'attente court",
      normal: "Temps d'attente moyen",
      peak: "Beaucoup de commandes, temps d'attente long"
    },
    days: {
      monday: 'Lundi',
      tuesday: 'Mardi',
      wednesday: 'Mercredi',
      thursday: 'Jeudi',
      friday: 'Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche'
    },
    timeSlots: {
      morning: 'Matin (6-12h)',
      afternoon: 'Après-midi (12-18h)',
      evening: 'Soir (18-24h)',
      lateNight: 'Nuit (24-6h)'
    },
    minutes: 'min',
    peak: 'Pointe',
    normal: 'Normal',
    quiet: 'Calme'
  },
  zh: {
    title: '平均烹饪时间',
    subtitle: '查看各天和时段的平均烹饪时间',
    today: '今天',
    statusGuide: '状态说明',
    statusDescriptions: {
      quiet: '等待时间较短',
      normal: '等待时间正常',
      peak: '订单较多，等待时间较长'
    },
    days: {
      monday: '星期一',
      tuesday: '星期二',
      wednesday: '星期三',
      thursday: '星期四',
      friday: '星期五',
      saturday: '星期六',
      sunday: '星期日'
    },
    timeSlots: {
      morning: '上午 (6-12时)',
      afternoon: '下午 (12-18时)',
      evening: '晚上 (18-24时)',
      lateNight: '深夜 (24-6时)'
    },
    minutes: '分钟',
    peak: '高峰',
    normal: '正常',
    quiet: '空闲'
  },
  ja: {
    title: '平均調理時間',
    subtitle: '曜日・時間帯別の平均調理時間をご確認ください',
    today: '今日',
    statusGuide: 'ステータス案内',
    statusDescriptions: {
      quiet: '待ち時間が短いです',
      normal: '平均的な待ち時間です',
      peak: '注文が多く待ち時間が長いです'
    },
    days: {
      monday: '月曜日',
      tuesday: '火曜日',
      wednesday: '水曜日',
      thursday: '木曜日',
      friday: '金曜日',
      saturday: '土曜日',
      sunday: '日曜日'
    },
    timeSlots: {
      morning: '午前 (6-12時)',
      afternoon: '午後 (12-18時)',
      evening: '夜 (18-24時)',
      lateNight: '深夜 (24-6時)'
    },
    minutes: '分',
    peak: 'ピーク',
    normal: '普通',
    quiet: '空き'
  },
  hi: {
    title: 'औसत खाना पकाने का समय',
    subtitle: 'दिन और समय के अनुसार औसत पकाने का समय देखें',
    today: 'आज',
    statusGuide: 'स्थिति गाइड',
    statusDescriptions: {
      quiet: 'प्रतीक्षा समय कम है',
      normal: 'औसत प्रतीक्षा समय है',
      peak: 'ऑर्डर अधिक हैं, प्रतीक्षा समय लंबा है'
    },
    days: {
      monday: 'सोमवार',
      tuesday: 'मंगलवार',
      wednesday: 'बुधवार',
      thursday: 'गुरुवार',
      friday: 'शुक्रवार',
      saturday: 'शनिवार',
      sunday: 'रविवार'
    },
    timeSlots: {
      morning: 'सुबह (6-12 बजे)',
      afternoon: 'दोपहर (12-18 बजे)',
      evening: 'शाम (18-24 बजे)',
      lateNight: 'देर रात (24-6 बजे)'
    },
    minutes: 'मिनट',
    peak: 'पीक',
    normal: 'सामान्य',
    quiet: 'शांत'
  },
  de: {
    title: 'Durchschnittliche Zubereitungszeit',
    subtitle: 'Sehen Sie die durchschnittlichen Zubereitungszeiten nach Tag und Uhrzeit',
    today: 'Heute',
    statusGuide: 'Status-Übersicht',
    statusDescriptions: {
      quiet: 'Kurze Wartezeit',
      normal: 'Durchschnittliche Wartezeit',
      peak: 'Viele Bestellungen, lange Wartezeit'
    },
    days: {
      monday: 'Montag',
      tuesday: 'Dienstag',
      wednesday: 'Mittwoch',
      thursday: 'Donnerstag',
      friday: 'Freitag',
      saturday: 'Samstag',
      sunday: 'Sonntag'
    },
    timeSlots: {
      morning: 'Morgen (6-12 Uhr)',
      afternoon: 'Nachmittag (12-18 Uhr)',
      evening: 'Abend (18-24 Uhr)',
      lateNight: 'Nacht (24-6 Uhr)'
    },
    minutes: 'Min.',
    peak: 'Stoßzeit',
    normal: 'Normal',
    quiet: 'Ruhig'
  },
  it: {
    title: 'Tempo medio di preparazione',
    subtitle: 'Consulta i tempi medi di preparazione per giorno e orario',
    today: 'Oggi',
    statusGuide: 'Guida agli stati',
    statusDescriptions: {
      quiet: 'Tempo di attesa breve',
      normal: 'Tempo di attesa nella media',
      peak: 'Molti ordini, tempo di attesa lungo'
    },
    days: {
      monday: 'Lunedì',
      tuesday: 'Martedì',
      wednesday: 'Mercoledì',
      thursday: 'Giovedì',
      friday: 'Venerdì',
      saturday: 'Sabato',
      sunday: 'Domenica'
    },
    timeSlots: {
      morning: 'Mattina (6-12)',
      afternoon: 'Pomeriggio (12-18)',
      evening: 'Sera (18-24)',
      lateNight: 'Notte (24-6)'
    },
    minutes: 'min',
    peak: 'Picco',
    normal: 'Normale',
    quiet: 'Tranquillo'
  },
  ru: {
    title: 'Среднее время приготовления',
    subtitle: 'Узнайте среднее время приготовления по дням и часам',
    today: 'Сегодня',
    statusGuide: 'Описание статусов',
    statusDescriptions: {
      quiet: 'Короткое время ожидания',
      normal: 'Среднее время ожидания',
      peak: 'Много заказов, долгое время ожидания'
    },
    days: {
      monday: 'Понедельник',
      tuesday: 'Вторник',
      wednesday: 'Среда',
      thursday: 'Четверг',
      friday: 'Пятница',
      saturday: 'Суббота',
      sunday: 'Воскресенье'
    },
    timeSlots: {
      morning: 'Утро (6-12)',
      afternoon: 'День (12-18)',
      evening: 'Вечер (18-24)',
      lateNight: 'Ночь (24-6)'
    },
    minutes: 'мин',
    peak: 'Пик',
    normal: 'Обычно',
    quiet: 'Свободно'
  },
  ar: {
    title: 'متوسط وقت التحضير',
    subtitle: 'اطّلع على متوسط أوقات التحضير حسب اليوم والساعة',
    today: 'اليوم',
    statusGuide: 'دليل الحالات',
    statusDescriptions: {
      quiet: 'وقت الانتظار قصير',
      normal: 'وقت الانتظار متوسط',
      peak: 'الطلبات كثيرة ووقت الانتظار طويل'
    },
    days: {
      monday: 'الاثنين',
      tuesday: 'الثلاثاء',
      wednesday: 'الأربعاء',
      thursday: 'الخميس',
      friday: 'الجمعة',
      saturday: 'السبت',
      sunday: 'الأحد'
    },
    timeSlots: {
      morning: 'صباحًا (6-12)',
      afternoon: 'ظهرًا (12-18)',
      evening: 'مساءً (18-24)',
      lateNight: 'منتصف الليل (24-6)'
    },
    minutes: 'دقيقة',
    peak: 'ذروة',
    normal: 'عادي',
    quiet: 'هادئ'
  },
  vi: {
    title: 'Thời gian nấu trung bình',
    subtitle: 'Xem thời gian nấu trung bình theo ngày và khung giờ',
    today: 'Hôm nay',
    statusGuide: 'Hướng dẫn trạng thái',
    statusDescriptions: {
      quiet: 'Thời gian chờ ngắn',
      normal: 'Thời gian chờ trung bình',
      peak: 'Nhiều đơn hàng, thời gian chờ lâu'
    },
    days: {
      monday: 'Thứ Hai',
      tuesday: 'Thứ Ba',
      wednesday: 'Thứ Tư',
      thursday: 'Thứ Năm',
      friday: 'Thứ Sáu',
      saturday: 'Thứ Bảy',
      sunday: 'Chủ Nhật'
    },
    timeSlots: {
      morning: 'Sáng (6-12h)',
      afternoon: 'Chiều (12-18h)',
      evening: 'Tối (18-24h)',
      lateNight: 'Khuya (24-6h)'
    },
    minutes: 'phút',
    peak: 'Cao điểm',
    normal: 'Bình thường',
    quiet: 'Vắng'
  },
  th: {
    title: 'เวลาปรุงอาหารโดยเฉลี่ย',
    subtitle: 'ตรวจสอบเวลาปรุงอาหารเฉลี่ยตามวันและช่วงเวลา',
    today: 'วันนี้',
    statusGuide: 'คำอธิบายสถานะ',
    statusDescriptions: {
      quiet: 'เวลารอสั้น',
      normal: 'เวลารอปกติ',
      peak: 'มีออเดอร์จำนวนมาก เวลารอนาน'
    },
    days: {
      monday: 'วันจันทร์',
      tuesday: 'วันอังคาร',
      wednesday: 'วันพุธ',
      thursday: 'วันพฤหัสบดี',
      friday: 'วันศุกร์',
      saturday: 'วันเสาร์',
      sunday: 'วันอาทิตย์'
    },
    timeSlots: {
      morning: 'เช้า (6-12 น.)',
      afternoon: 'บ่าย (12-18 น.)',
      evening: 'เย็น (18-24 น.)',
      lateNight: 'ดึก (24-6 น.)'
    },
    minutes: 'นาที',
    peak: 'ชั่วโมงเร่งด่วน',
    normal: 'ปกติ',
    quiet: 'ว่าง'
  },
  nl: {
    title: 'Gemiddelde bereidingstijd',
    subtitle: 'Bekijk de gemiddelde bereidingstijden per dag en tijdstip',
    today: 'Vandaag',
    statusGuide: 'Statusgids',
    statusDescriptions: {
      quiet: 'Korte wachttijd',
      normal: 'Gemiddelde wachttijd',
      peak: 'Veel bestellingen, lange wachttijd'
    },
    days: {
      monday: 'Maandag',
      tuesday: 'Dinsdag',
      wednesday: 'Woensdag',
      thursday: 'Donderdag',
      friday: 'Vrijdag',
      saturday: 'Zaterdag',
      sunday: 'Zondag'
    },
    timeSlots: {
      morning: 'Ochtend (6-12u)',
      afternoon: 'Middag (12-18u)',
      evening: 'Avond (18-24u)',
      lateNight: 'Nacht (24-6u)'
    },
    minutes: 'min',
    peak: 'Piek',
    normal: 'Normaal',
    quiet: 'Rustig'
  }
};

export const cookingTimeData: CookingTimeData = {
  monday: {
    morning: { time: 15, status: 'normal' },
    afternoon: { time: 25, status: 'peak' },
    evening: { time: 20, status: 'normal' },
    lateNight: { time: 12, status: 'quiet' }
  },
  tuesday: {
    morning: { time: 14, status: 'normal' },
    afternoon: { time: 22, status: 'peak' },
    evening: { time: 18, status: 'normal' },
    lateNight: { time: 11, status: 'quiet' }
  },
  wednesday: {
    morning: { time: 16, status: 'normal' },
    afternoon: { time: 28, status: 'peak' },
    evening: { time: 23, status: 'peak' },
    lateNight: { time: 13, status: 'quiet' }
  },
  thursday: {
    morning: { time: 15, status: 'normal' },
    afternoon: { time: 24, status: 'peak' },
    evening: { time: 21, status: 'normal' },
    lateNight: { time: 12, status: 'quiet' }
  },
  friday: {
    morning: { time: 17, status: 'normal' },
    afternoon: { time: 30, status: 'peak' },
    evening: { time: 35, status: 'peak' },
    lateNight: { time: 25, status: 'peak' }
  },
  saturday: {
    morning: { time: 20, status: 'normal' },
    afternoon: { time: 32, status: 'peak' },
    evening: { time: 38, status: 'peak' },
    lateNight: { time: 28, status: 'peak' }
  },
  sunday: {
    morning: { time: 18, status: 'normal' },
    afternoon: { time: 26, status: 'peak' },
    evening: { time: 22, status: 'normal' },
    lateNight: { time: 15, status: 'quiet' }
  }
};

export function getBarColor(time: number) {
  if (time <= 15) return { bg: 'bg-primary', text: 'text-primary' };
  if (time <= 30) return { bg: 'bg-warning', text: 'text-warning' };
  return { bg: 'bg-secondary', text: 'text-secondary' };
}

export function getBarWidth(time: number) {
  const maxTime = 60;
  return Math.min((time / maxTime) * 100, 100);
}

export function getCurrentDay(): keyof CookingTimeData {
  const days: (keyof CookingTimeData)[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date().getDay()];
}
